<template>
  <div class="credit-pack-form" data-testid="credit-pack-form">
    <form @submit.prevent="handleSubmit">
      <div class="form-fields">
        <input-text
          v-model="formData.name"
          elem-id="credit-pack-name"
          :label="t('Nome')"
          data-testid="credit-pack-name-input"
          class="field-full"
        />
        <input-number
          v-model="formData.credits"
          elem-id="credit-pack-credits"
          :label="t('Créditos')"
          data-testid="credit-pack-credits-input"
          :min="1"
        />
        <input-number
          v-model="formData.sort_order"
          elem-id="credit-pack-sort-order"
          :label="t('Ordem')"
          data-testid="credit-pack-sort-order-input"
          :min="0"
        />
        <input-toggle
          v-model="activeBool"
          elem-id="credit-pack-active"
          :label="t('Ativo')"
          data-testid="credit-pack-active-toggle"
        />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../../eventBus'
import { useCreditPacks } from '../../../../composables/useCreditPacks'
import { MICRO_PER_CREDIT } from '../../../../composables/useCreditWallet'
import { emitValidationFailed } from '../../../../composables/useFormValidation'
import { log } from '../../../../utils/DebugLogger'
import type { AiCredits_PackRow } from '../../../../contracts/AiCredits_PackRow'
import type { AiCredits_PackInput } from '../../../../contracts/AiCredits_PackInput'

const { t } = useI18n()
const { createPack, updatePack } = useCreditPacks()

const props = defineProps<{
  // Partial: an id-less prefill (e.g. from the pricing calculator, credits only)
  // has no id/name/sort_order/active — only a real edit-mode pack carries all of them.
  pack?: Partial<AiCredits_PackRow> | null
}>()

const emit = defineEmits<{
  (e: 'saved', payload: { id: number; created: boolean }): void
  (e: 'validation-failed'): void
}>()

/**
 * UNIT BOUNDARY — the field is "Créditos" and holds CREDITS, while
 * ai_credit_packs.credits is stored (and granted) in MICRO-credits. The
 * franqueado-facing PackPurchaseModal already renders it via formatCredits(),
 * which divides by MICRO_PER_CREDIT — this form is the write side of that
 * same contract and must multiply.
 *
 * Sending the typed number raw makes a "100 créditos" pack grant 100 µcr
 * (0.0001 credits). Same defect as StaffSubscriptionPlanForm, which under-granted
 * a 50-credit subscription plan by 1e6 (franchise 14, 2026-08-11).
 */
interface PackFormData {
  name: string
  /** CREDITS as typed by the user — converted to µcr on submit. */
  credits: number | null
  sort_order: number
  active: number
}

const formData = reactive<PackFormData>({
  name: '',
  credits: null,
  sort_order: 0,
  active: 1,
})

const isSaving = ref(false)

// Map active integer ↔ boolean for InputToggle
const activeBool = computed({
  get: () => formData.active === 1,
  set: (v: boolean) => { formData.active = v ? 1 : 0 },
})

// pack is Partial: a real edit-mode pack carries every field, an id-less prefill
// (e.g. from the pricing calculator) may carry only `credits` — missing fields fall
// back to the same defaults resetForm() uses.
function populateFromPack(pack: Partial<AiCredits_PackRow>): void {
  formData.name = pack.name ?? ''
  // µcr (stored) → credits (displayed)
  formData.credits = pack.credits != null ? pack.credits / MICRO_PER_CREDIT : null
  formData.sort_order = pack.sort_order ?? 0
  formData.active = pack.active ?? 1
}

function resetForm(): void {
  formData.name = ''
  formData.credits = null
  formData.sort_order = 0
  formData.active = 1
}

function validateForm(): boolean {
  const errors: Record<string, string> = {}

  if (!formData.name.trim()) {
    errors.name = t('Nome é obrigatório')
  }
  if (formData.credits === null || formData.credits < 1) {
    errors.credits = t('Créditos são obrigatórios e devem ser pelo menos 1')
  }

  if (Object.keys(errors).length > 0) {
    const firstError = Object.values(errors)[0]
    eventBus.emit('globalMessage.error', firstError)
    emitValidationFailed('CreditPackForm', errors)
    emit('validation-failed')
    return false
  }
  return true
}

async function handleSubmit(): Promise<void> {
  if (!validateForm()) return
  try {
    isSaving.value = true
    const input: AiCredits_PackInput = {
      name: formData.name,
      // credits (typed) → µcr (stored/granted). See PackFormData.
      credits: Math.round(formData.credits! * MICRO_PER_CREDIT),
      sort_order: formData.sort_order,
      active: formData.active,
    }
    let savedId: number
    let created: boolean
    if (props.pack?.id != null) {
      await updatePack(props.pack.id, input)
      savedId = props.pack.id
      created = false
    } else {
      savedId = await createPack(input)
      created = true
    }
    log('CreditPackForm', 'handleSubmit', 'Pack salvo', { id: savedId, created })
    eventBus.emit('globalMessage.success', t('Pacote salvo com sucesso'))
    emit('saved', { id: savedId, created })
  } catch (err) {
    log('CreditPackForm', 'handleSubmit', 'Erro ao salvar pack', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar pacote'))
  } finally {
    isSaving.value = false
  }
}

// Expose for parent Modal to trigger submit and track state
defineExpose({
  handleSubmit,
  isSaving,
  resetForm,
  populateFromPack,
})

watch(
  () => props.pack,
  (pack) => {
    if (pack) {
      populateFromPack(pack)
    } else {
      resetForm()
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.form-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
}

.field-full {
  flex: 1 1 100%;
}
</style>
